import React, { useEffect, useState } from 'react';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { supabase } from '../supabaseClient';
import './Resumes.css';

const Resumes = () => {
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedResume, setSelectedResume] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [activeResume, setActiveResume] = useState(null);

  useEffect(() => {
    fetchResumes();
  }, []);

  useEffect(() => {
    // Cleanup URL when component unmounts or selectedResume changes
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const fetchResumes = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('resumes')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setResumes(data || []);
      
      // Set the active resume to the most recent one with status 'active'
      const active = (data || []).find(r => r.status === 'active');
      if (active) {
        setActiveResume(active);
      } else if (data && data.length > 0) {
        setActiveResume(data[0]);
      }
    } catch (error) {
      console.error('Error fetching resumes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResumeClick = async (resume) => {
    setSelectedResume(resume);
    
    try {
      // Download the file from storage
      const { data, error } = await supabase.storage
        .from('resume')
        .download(resume.file_path);

      if (error) throw error;

      // Create a URL for the blob
      const url = URL.createObjectURL(data);
      setPdfUrl(url);
    } catch (error) {
      console.error('Error loading PDF:', error);
    }
  };

  const closeSidePanel = () => {
    setSelectedResume(null);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderProfile = () => {
    if (!activeResume) {
      return (
        <div className="empty-state">
          <p>No resume selected.</p>
        </div>
      );
    }

    if (!activeResume.parsed_data) {
      return (
        <div className="empty-state">
          <p>Resume analysis is in progress. Please check back in a moment.</p>
        </div>
      );
    }

    const data = activeResume.parsed_data;

    return (
      <div className="profile-view">
        {/* Personal Info */}
        {data.personal_info && (
          <div className="profile-section">
            <h2 className="profile-section-title">Personal Information</h2>
            <div className="profile-info-grid">
              {data.personal_info.name && (
                <div className="profile-info-item">
                  <span className="profile-label">Name</span>
                  <span className="profile-value">{data.personal_info.name}</span>
                </div>
              )}
              {data.personal_info.email && (
                <div className="profile-info-item">
                  <span className="profile-label">Email</span>
                  <span className="profile-value">{data.personal_info.email}</span>
                </div>
              )}
              {data.personal_info.phone && (
                <div className="profile-info-item">
                  <span className="profile-label">Phone</span>
                  <span className="profile-value">{data.personal_info.phone}</span>
                </div>
              )}
              {data.personal_info.location && (
                <div className="profile-info-item">
                  <span className="profile-label">Location</span>
                  <span className="profile-value">{data.personal_info.location}</span>
                </div>
              )}
              {data.personal_info.linkedin && (
                <div className="profile-info-item">
                  <span className="profile-label">LinkedIn</span>
                  <span className="profile-value">{data.personal_info.linkedin}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Summary */}
        {data.summary && (
          <div className="profile-section">
            <h2 className="profile-section-title">Professional Summary</h2>
            <p className="profile-summary">{data.summary}</p>
          </div>
        )}

        {/* Skills */}
        {data.skills && (data.skills.technical?.length > 0 || data.skills.soft?.length > 0 || data.skills.tools?.length > 0) && (
          <div className="profile-section">
            <h2 className="profile-section-title">Skills</h2>
            <div className="skills-grid">
              {data.skills.technical?.length > 0 && (
                <div className="skill-category">
                  <h3 className="skill-category-title">Technical Skills</h3>
                  <div className="skill-tags">
                    {data.skills.technical.map((skill, index) => (
                      <span key={index} className="skill-tag">{skill}</span>
                    ))}
                  </div>
                </div>
              )}
              {data.skills.tools?.length > 0 && (
                <div className="skill-category">
                  <h3 className="skill-category-title">Tools & Technologies</h3>
                  <div className="skill-tags">
                    {data.skills.tools.map((tool, index) => (
                      <span key={index} className="skill-tag">{tool}</span>
                    ))}
                  </div>
                </div>
              )}
              {data.skills.soft?.length > 0 && (
                <div className="skill-category">
                  <h3 className="skill-category-title">Soft Skills</h3>
                  <div className="skill-tags">
                    {data.skills.soft.map((skill, index) => (
                      <span key={index} className="skill-tag">{skill}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Work Experience */}
        {data.work_experience && data.work_experience.length > 0 && (
          <div className="profile-section">
            <h2 className="profile-section-title">Work Experience</h2>
            <div className="experience-list">
              {data.work_experience.map((exp, index) => (
                <div key={index} className="experience-item">
                  <div className="experience-header">
                    <div>
                      <h3 className="experience-title">{exp.title}</h3>
                      <p className="experience-company">{exp.company}</p>
                    </div>
                    <span className="experience-date">{exp.start_date} - {exp.end_date}</span>
                  </div>
                  {exp.description && <p className="experience-description">{exp.description}</p>}
                  {exp.key_achievements && exp.key_achievements.length > 0 && (
                    <ul className="experience-achievements">
                      {exp.key_achievements.map((achievement, i) => (
                        <li key={i}>{achievement}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Education */}
        {data.education && data.education.length > 0 && (
          <div className="profile-section">
            <h2 className="profile-section-title">Education</h2>
            <div className="education-list">
              {data.education.map((edu, index) => (
                <div key={index} className="education-item">
                  <div className="education-header">
                    <div>
                      <h3 className="education-degree">{edu.degree}</h3>
                      <p className="education-school">{edu.school}</p>
                      {edu.field && <p className="education-field">{edu.field}</p>}
                    </div>
                    <span className="education-date">{edu.graduation_date}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Certifications */}
        {data.certifications && data.certifications.length > 0 && (
          <div className="profile-section">
            <h2 className="profile-section-title">Certifications</h2>
            <ul className="certification-list">
              {data.certifications.map((cert, index) => (
                <li key={index} className="certification-item">{cert}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Languages */}
        {data.languages && data.languages.length > 0 && (
          <div className="profile-section">
            <h2 className="profile-section-title">Languages</h2>
            <div className="language-tags">
              {data.languages.map((lang, index) => (
                <span key={index} className="language-tag">{lang}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="resumes-page">
        <div className="resumes-container">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="resumes-page">
      <div className={`resumes-container ${selectedResume ? 'with-panel' : ''}`}>
        <div className="resumes-content">
          <h1>Resumes</h1>
          <p className="resumes-subtitle">View your resume profile and manage files</p>

          {/* Tabs */}
          <div className="tabs">
            <button
              className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              Profile
            </button>
            <button
              className={`tab ${activeTab === 'files' ? 'active' : ''}`}
              onClick={() => setActiveTab('files')}
            >
              Files
            </button>
          </div>

          {/* Tab Content */}
          {resumes.length === 0 ? (
            <div className="empty-state">
              <p>No resumes uploaded yet.</p>
            </div>
          ) : (
            <div className="tab-content">
              {activeTab === 'profile' ? (
                renderProfile()
              ) : (
                <div className="resumes-list">
                  {resumes.map((resume) => (
                    <div
                      key={resume.id}
                      className={`resume-item ${selectedResume?.id === resume.id ? 'selected' : ''}`}
                      onClick={() => handleResumeClick(resume)}
                    >
                      <div className="resume-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <div className="resume-info">
                        <h3>{resume.file_name}</h3>
                        <p className="resume-date">{formatDate(resume.created_at)}</p>
                      </div>
                      <span className={`status-badge ${resume.status}`}>
                        {resume.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Side Panel - Only for Files tab */}
      {selectedResume && activeTab === 'files' && (
        <div className="side-panel">
          <div className="side-panel-header">
            <div className="side-panel-title">
              <h2>{selectedResume.file_name}</h2>
              <span className={`status-badge ${selectedResume.status}`}>
                {selectedResume.status}
              </span>
            </div>
            <button className="close-button" onClick={closeSidePanel}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          <div className="side-panel-content">
            {pdfUrl ? (
              <Worker workerUrl={`https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`}>
                <div className="pdf-viewer">
                  <Viewer fileUrl={pdfUrl} />
                </div>
              </Worker>
            ) : (
              <div className="loading-pdf">Loading PDF...</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Resumes;

